import { isVideoMediaFieldKey } from "./types";
import type { TemplateFieldDef } from "./flyerTemplateDevService";

const INPUT_TIP_MARKER = "\n\n💡 Replace";

export interface FlyerTemplateLike {
    fields: TemplateFieldDef[];
    prompt_template?: string;
}

/** Strip the UI hint suffix from the chat input before sending to the API. */
export function stripTemplateInputTip(text: string): string {
    const idx = text.indexOf(INPUT_TIP_MARKER);
    return (idx >= 0 ? text.slice(0, idx) : text).trim();
}

const CONTEXT_WINDOW = 30; // chars of surrounding text used as anchors

/**
 * For a template like "...for {destination} starting from {price}...",
 * extract what the user typed in place of each {key} by anchoring on the
 * fixed text immediately before and after the placeholder.
 */
function extractByInlineReplacement(
    template: string,
    customPrompt: string,
    key: string,
): string | null {
    const placeholder = `{${key}}`;
    const idx = template.indexOf(placeholder);
    if (idx === -1) return null;
    // Already present in prompt → not replaced yet
    if (customPrompt.includes(placeholder)) return null;

    const before = template.slice(Math.max(0, idx - CONTEXT_WINDOW), idx);
    const after = template.slice(idx + placeholder.length, idx + placeholder.length + CONTEXT_WINDOW);

    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Build regex: optional leading anchor + capture group + optional trailing anchor
    const parts: string[] = [];
    if (before.trim()) parts.push(`${esc(before.slice(-15))}\\s*`);
    parts.push(`(.+?)`);
    if (after.trim()) parts.push(`\\s*${esc(after.slice(0, 15))}`);

    try {
        const m = customPrompt.match(new RegExp(parts.join(""), "i"));
        const val = m?.[1]?.trim();
        return val && val.length <= 200 ? val : null;
    } catch {
        return null;
    }
}

/**
 * Parse the filled chat input into custom_prompt (and optional local field hints).
 * POST /rag/generate-from-template accepts only template_id + custom_prompt; the
 * backend extracts destination, price, etc. from the prompt text.
 *
 * Extraction priority:
 *  1. "Label: value" pattern in the prompt (explicit key–value pair)
 *  2. Inline replacement — user typed their answer directly where {key} was
 */
export function buildFlyerTemplateFieldsPayload(
    tmpl: FlyerTemplateLike,
    filledInput: string,
): Record<string, string> {
    const customPrompt = stripTemplateInputTip(filledInput);
    const payload: Record<string, string> = { custom_prompt: customPrompt };
    const template = tmpl.prompt_template || "";

    for (const field of tmpl.fields) {
        if (isVideoMediaFieldKey(field.key)) continue;

        const label = field.label.trim();

        // 1. "Label: value" — explicit key–value pair in the prompt
        if (label) {
            const labelEsc = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const byLabel = customPrompt.match(
                new RegExp(`${labelEsc}\\s*[:：]\\s*([^\\n|.;]+)`, "i"),
            );
            if (byLabel?.[1]?.trim()) {
                payload[field.key] = byLabel[1].trim();
                continue;
            }
        }

        // 2. Inline replacement — user typed their answer where {key} was
        const inlineVal = extractByInlineReplacement(template, customPrompt, field.key);
        if (inlineVal) {
            payload[field.key] = inlineVal;
        }
    }

    return payload;
}
